/**
 * Centralized logging service for the Risk Game application
 * Provides structured logging with different levels and contextual information
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  data?: Record<string, unknown>;
  error?: Error;
}

export interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableStorage: boolean;
  maxStorageEntries: number;
}

class LoggingService {
  private config: LoggerConfig;
  private logEntries: LogEntry[] = [];

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO,
      enableConsole: true,
      enableStorage: process.env.NODE_ENV === 'development',
      maxStorageEntries: 1000,
      ...config
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, unknown>,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      data,
      error
    };
  }

  private formatMessage(entry: LogEntry): string {
    const levelName = LogLevel[entry.level];
    const contextStr = entry.context ? `[${entry.context}]` : '';
    const dataStr = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
    return `${entry.timestamp} ${levelName} ${contextStr} ${entry.message}${dataStr}`;
  }

  private writeToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const formattedMessage = this.formatMessage(entry);
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, entry.data);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, entry.data);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, entry.data);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, entry.data, entry.error);
        break;
    }
  }

  private storeEntry(entry: LogEntry): void {
    if (!this.config.enableStorage) return;

    this.logEntries.push(entry);
    
    // Trim entries if we exceed max storage
    if (this.logEntries.length > this.config.maxStorageEntries) {
      this.logEntries.splice(0, this.logEntries.length - this.config.maxStorageEntries);
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: string,
    data?: Record<string, unknown>,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createLogEntry(level, message, context, data, error);
    
    this.writeToConsole(entry);
    this.storeEntry(entry);
  }

  /**
   * Log debug information - only shown in development
   */
  debug(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context, data);
  }

  /**
   * Log general information
   */
  info(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  /**
   * Log warnings that don't break functionality
   */
  warn(message: string, context?: string, data?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  /**
   * Log errors that affect functionality
   */
  error(message: string, context?: string, data?: Record<string, unknown>, error?: Error): void {
    this.log(LogLevel.ERROR, message, context, data, error);
  }

  /**
   * Get all stored log entries
   */
  getLogEntries(): LogEntry[] {
    return [...this.logEntries];
  }

  /**
   * Clear all stored log entries
   */
  clearLogs(): void {
    this.logEntries = [];
  }

  /**
   * Update logger configuration
   */
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Export logs as JSON for debugging
   */
  exportLogs(): string {
    return JSON.stringify(this.logEntries, null, 2);
  }

  /**
   * Performance timing helper
   */
  time(label: string, context?: string): () => void {
    const startTime = performance.now();
    this.debug(`Timer started: ${label}`, context);
    
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      this.debug(`Timer ended: ${label}`, context, { duration: `${duration.toFixed(2)}ms` });
    };
  }
}

// Create singleton instance
export const logger = new LoggingService();

// Export convenience functions for common usage patterns
export const logGameAction = (action: string, data?: Record<string, unknown>) => {
  logger.info(action, 'GameAction', data);
};

export const logPerformance = (operation: string, duration: number, context?: string) => {
  logger.debug(`Performance: ${operation}`, context, { duration: `${duration.toFixed(2)}ms` });
};

export const logError = (error: Error, context?: string, data?: Record<string, unknown>) => {
  logger.error(error.message, context, data, error);
};

export default logger;